// ============================================================================
// ROUTE ERROR BOUNDARY
//
// Catches render errors inside a page so a single crash doesn't unmount the
// entire app (the symptom: every parent + sibling re-renders to empty, and
// the screen goes black with no explanation). On catch we show the actual
// error message + a "Reload page" affordance, and log a structured warning
// the user can copy/paste back to support.
//
// Used at the page level (e.g. wrapped around SignalsPage). For finer-grained
// isolation, wrap individual heavy widgets in their own boundary.
// ============================================================================

import { Component } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Structured log so a future telemetry hook can pick this up without
    // changes here. Stack + componentStack are the two useful fields.
    console.error('[ErrorBoundary] React render crash:', {
      message:        error?.message,
      stack:          error?.stack,
      componentStack: info?.componentStack,
      route:          this.props.routeLabel || 'unknown',
    });
  }

  handleReload = () => {
    // Hard reload — clears any in-memory state corruption that caused the crash.
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div style={{
        minHeight: 320, padding: 28, borderRadius: 12,
        background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.25)',
        color: '#cbd5e1', fontFamily: 'inherit',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <AlertTriangle size={20} color="#ef4444" />
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#ef4444' }}>This page hit a render error</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
              The rest of the app is still safe. Reload to recover.
            </div>
          </div>
        </div>
        <pre style={{
          background: 'rgba(15,23,42,0.7)', padding: '10px 12px', borderRadius: 8,
          fontSize: 11, color: '#fca5a5', overflow: 'auto',
          maxHeight: 200, margin: '0 0 14px 0', whiteSpace: 'pre-wrap',
        }}>{this.state.error?.message || String(this.state.error)}</pre>
        <button
          onClick={this.handleReload}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 18px',
            borderRadius: 7, border: '1px solid rgba(99,102,241,0.3)',
            background: 'rgba(99,102,241,0.08)', color: '#818cf8',
            fontSize: 12, fontFamily: 'inherit', cursor: 'pointer',
          }}
        >
          <RefreshCw size={12} />
          Reload page
        </button>
      </div>
    );
  }
}
